import { zodResolver } from '@hookform/resolvers/zod';
import { Password } from '@horevia/contracts';
import { Button, FormField, Notice, PasswordInput } from '@horevia/ui';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthLayout } from '@/features/auth/AuthLayout';
import { resetPassword } from '@/features/auth/queries';
import { ApiRequestError } from '@/lib/api';
import { errorMessage } from '@/lib/errors';

export const Route = createFileRoute('/reset-password')({
  validateSearch: z.object({ token: z.string().optional() }),
  component: ResetPasswordPage,
});

const FormSchema = z
  .object({ password: Password, confirm: z.string() })
  .refine((values) => values.password === values.confirm, {
    path: ['confirm'],
    message: 'mismatch',
  });
type FormValues = z.infer<typeof FormSchema>;

function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const { token } = Route.useSearch();
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { password: '', confirm: '' },
    mode: 'onBlur',
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    if (!token) {
      setFormError(t('auth:reset.invalidToken'));
      return;
    }
    try {
      await resetPassword({ token, password: values.password });
      setDone(true);
    } catch (error) {
      setFormError(
        error instanceof ApiRequestError &&
          (error.code === 'TOKEN_INVALID' || error.code === 'INVITATION_INVALID')
          ? t('auth:reset.invalidToken')
          : errorMessage(error, t),
      );
    }
  });

  if (done) {
    return (
      <AuthLayout title={t('auth:reset.successTitle')}>
        <div className="stack">
          <Notice tone="success">{t('auth:reset.successBody')}</Notice>
          <Button asChild variant="primary" block>
            <Link to="/login">{t('auth:login.submit')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth:reset.title')} subtitle={t('auth:reset.subtitle')}>
      <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
        {!token ? <Notice tone="warning">{t('auth:reset.invalidToken')}</Notice> : null}
        {formError ? (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        ) : null}

        <FormField
          label={t('auth:reset.password')}
          hint={t('common:validation.passwordPolicy')}
          error={form.formState.errors.password ? t('common:validation.passwordPolicy') : undefined}
          required
          requiredLabel={t('common:fields.required')}
        >
          {(field) => (
            <PasswordInput
              id={field.id}
              autoComplete="new-password"
              icon={<Lock className="ico" aria-hidden="true" />}
              invalid={field.invalid}
              aria-describedby={field.describedBy}
              showLabel={t('common:actions.showPassword')}
              hideLabel={t('common:actions.hidePassword')}
              {...form.register('password')}
            />
          )}
        </FormField>

        <FormField
          label={t('auth:reset.confirm')}
          error={
            form.formState.errors.confirm ? t('common:validation.passwordMismatch') : undefined
          }
          required
          requiredLabel={t('common:fields.required')}
        >
          {(field) => (
            <PasswordInput
              id={field.id}
              autoComplete="new-password"
              icon={<Lock className="ico" aria-hidden="true" />}
              invalid={field.invalid}
              aria-describedby={field.describedBy}
              showLabel={t('common:actions.showPassword')}
              hideLabel={t('common:actions.hidePassword')}
              {...form.register('confirm')}
            />
          )}
        </FormField>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={form.formState.isSubmitting}
          disabled={!token}
        >
          {t('auth:reset.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
